'use client';

import { clsx } from 'clsx';
import { FC, Fragment, ReactNode, useEffect, useRef, useState } from 'react';

import { lockScroll, unlockScroll } from 'src/general/helpers/scrollLock';

import { ButtonIcon } from '../Button/ButtonIcon';
import { ScrollBox } from '../ScrollBox/ScrollBox';

interface Props {
  id: string;
  title: ReactNode;
  titleChip?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  hideTitle?: boolean;
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
}

/**
 * Do not use this component directly, please use BasicModal
 */
export const BasicModalDialog: FC<Props> = ({
  id,
  title,
  titleChip,
  children,
  footer,
  hideTitle,
  isOpen,
  onClose,
  className,
}) => {
  // Prevent error trigger on iOS Safari ~16.4 causing a crash if tyring to open the modal twice
  const isFirstOpen = useRef<boolean>(false);
  const modalRef = useRef<HTMLDialogElement | null>(null);
  // Force redraw is required to allow the ScrollBox element to properly initialise once the modal is displayed
  const [forceRedraw, setForceRedraw] = useState<number>(0);

  const handleCloseModal = () => {
    isFirstOpen.current = false;
    if (onClose) {
      onClose();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      handleCloseModal();
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    const dialogDimensions = modalRef.current?.getBoundingClientRect();
    if (
      e.detail > 0 &&
      dialogDimensions &&
      (e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom)
    ) {
      handleCloseModal();
    }
  };

  useEffect(() => {
    if (isOpen) {
      lockScroll();
      modalRef.current?.showModal();
      isFirstOpen.current = true;
    }

    modalRef.current?.addEventListener('click', (e) => {
      handleClickOutside(e);
    });

    return () => modalRef.current?.removeEventListener('click', (e) => handleClickOutside(e));
  }, []);

  useEffect(() => {
    const modalElement = modalRef.current;
    if (modalElement) {
      if (isOpen) {
        setForceRedraw((value) => value + 1);
        if (!isFirstOpen.current) {
          lockScroll();
          modalElement.showModal();
        }
      } else {
        unlockScroll();
        modalElement.close();
      }
    }
    return () => {
      unlockScroll();
    };
  }, [isOpen]);

  return (
    <dialog
      ref={modalRef}
      className={clsx('BasicModal', className, 'modal')}
      aria-labelledby={`${id}-title`}
      data-testid={id}
      onKeyDown={handleKeyDown}
    >
      <section
        className={clsx('BasicModal-content', 'modalContent', {
          'BasicModal-withFooter': !!footer,
          'BasicModal-hideHeader': hideTitle,
        })}
      >
        <header className={clsx('BasicModal-header', 'modalHeader', { 'sr-only': hideTitle })}>
          <div className="BasicModal-headerTop">
            <h2 className="BasicModal-title" data-testid={`${id}-title`} id={`${id}-title`}>
              {title}
            </h2>
          </div>
          {titleChip}
        </header>
        <Fragment key={forceRedraw}>
          <ScrollBox className={clsx('BasicModal-contentMain', 'modalContentMain')}>{children}</ScrollBox>
        </Fragment>
        {!!footer && <footer className="BasicModal-footer">{footer}</footer>}
        {!!onClose && (
          <ButtonIcon
            iconName="close"
            title="Close"
            variant="tertiary"
            size="small"
            styling="round"
            onClick={handleCloseModal}
            data-testid="closeModal"
            className="BasicModal-close"
          />
        )}
      </section>
    </dialog>
  );
};
